import { Controller } from "@hotwired/stimulus";
// The /pure entry point defers injecting the js.stripe.com <script> until
// loadStripe() is called. The default entry injects it on import — i.e. on
// every page of the app, including the public marketing site — and Stripe's
// fraud-detection iframes (m.stripe.network) abort link-preview renderers
// (iMessage/LinkPresentation: "Frame load interrupted"), so no OG card ever
// shows. Keep Stripe confined to pages that actually mount this controller.
import { loadStripe } from "@stripe/stripe-js/pure";

export default class extends Controller {
  static values = {
    publishableKey: String,
    setupIntentUrl: String,
    returnUrl: String,
    dark: { type: Boolean, default: false },
  };

  static targets = ["element", "submit", "error", "form"];

  connect() {
    this.stripe = null;
    this.elements = null;
    this.paymentElement = null;
    if (this.hasElementTarget) {
      this.initialize();
    } else {
      console.warn("[payment-method-form] No element target found");
    }
  }

  async initialize() {
    if (this.stripe) return;

    const publishableKey = this.publishableKeyValue;
    const setupIntentUrl = this.setupIntentUrlValue;

    if (!publishableKey || !setupIntentUrl) {
      this.showError("Stripe configuration missing.");
      return;
    }

    this.disableSubmit();

    try {
      this.stripe = await loadStripe(publishableKey);
      if (!this.stripe) {
        this.showError("Failed to load Stripe.");
        return;
      }

      const clientSecret = await this.fetchClientSecret(setupIntentUrl);
      if (!clientSecret) return;

      // Dark mode: client-side DOM/OS is authoritative (drawer shares document with parent page).
      // Server cookie can be missing (incognito, first load), so we always check the DOM.
      const dataTheme = document.documentElement.getAttribute("data-theme");
      const hasDarkClass = document.documentElement.classList.contains("dark");
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      const clientSaysDark = dataTheme === "dark" || hasDarkClass || (dataTheme !== "light" && prefersDark);
      const serverSaysDark = this.hasDarkValue && this.darkValue;
      const isDarkMode = clientSaysDark || serverSaysDark;

      if (import.meta.env?.DEV) {
        console.debug("[payment-method-form] dark:", { dataTheme, hasDarkClass, prefersDark, clientSaysDark, serverSaysDark, isDarkMode });
      }

      // Use Stripe's built-in "night" theme for dark mode; custom variables can conflict
      const appearance = isDarkMode ? { theme: "night" } : { theme: "stripe" };

      this.elements = this.stripe.elements({ clientSecret, appearance });
      this.paymentElement = this.elements.create("payment", {
        wallets: { applePay: "never", googlePay: "never", link: "never" },
      });
      this.paymentElement.mount(this.elementTarget);
      this.enableSubmit();
    } catch (err) {
      console.error("[payment-method-form]", err);
      this.showError(err.message || "Failed to initialize payment form.");
    }
  }

  async fetchClientSecret(url) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
    const headers = {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    };
    if (csrfToken) headers["X-CSRF-Token"] = csrfToken;

    const response = await fetch(url, { method: "POST", headers });
    const contentType = response.headers.get("content-type");

    if (!contentType?.includes("application/json")) {
      this.showError("Setup failed: server returned non-JSON. Check Rails logs.");
      console.error("[payment-method-form] Unexpected response:", response.status, await response.text());
      return null;
    }

    const data = await response.json();
    if (!response.ok) {
      this.showError(data.error || "Failed to initialize.");
      return null;
    }
    return data.client_secret;
  }

  async submit(event) {
    event.preventDefault();
    if (!this.stripe || !this.elements) {
      this.showError("Payment form not ready. Please refresh.");
      return;
    }

    this.disableSubmit();
    this.hideError();

    const { setupIntent, error } = await this.stripe.confirmSetup({
      elements: this.elements,
      confirmParams: {
        return_url: this.returnUrlValue,
      },
      redirect: "if_required",
    });

    if (error) {
      this.showError(error.message || "Payment failed.");
      this.enableSubmit();
      return;
    }

    if (setupIntent?.status === "succeeded" && setupIntent.payment_method) {
      this.submitFormWithPaymentMethod(setupIntent.payment_method);
    } else {
      this.enableSubmit();
    }
  }

  submitFormWithPaymentMethod(paymentMethod) {
    const pmId = typeof paymentMethod === "string" ? paymentMethod : paymentMethod?.id;
    if (!pmId) return;
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "payment_method_id";
    input.value = pmId;
    this.formTarget.appendChild(input);
    this.formTarget.submit();
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.classList.remove("hidden");
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = "";
      this.errorTarget.classList.add("hidden");
    }
  }

  disableSubmit() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true;
    }
  }

  enableSubmit() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false;
    }
  }
}
